import { useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import SVGImage from '~/components/Image/SVGImage'
import ScoreboardActivityChart from '~/shared/components/Scoreboard/ScoreboardActivityChart'
import useRetakeActivity from '~/statistics/hooks/useRetakeActivity'
import ROUTES from '~/shared/constants/routes.const'

import ActivitySetProgress from '../CircularProgress'

type retakeBtnClickFunction = (setNumber: number) => void
interface Props {
  activityPath: string
  className?: string
  setCompletionPercentage: number
  levelNo: number
  locale: string | undefined
  retakeRound: string | null
  retakeBtnClick: retakeBtnClickFunction
  setNo: number
  correctPercentage: number
  id: string
}

export default function ActivitySet({
  activityPath,
  className = '',
  setCompletionPercentage,
  levelNo,
  locale,
  retakeRound,
  retakeBtnClick,
  setNo,
  correctPercentage,
  id,
}: Props): React.ReactElement {
  const { t } = useTranslation('statistics')
  const [showBarChartModal, setShowBarChartModal] = useState(false)
  const { onRetakeActivity, onContinueActivity } = useRetakeActivity(locale)
  const router = useRouter()
  const isFamilyView = router.asPath.includes(ROUTES.FAMILY_ADMIN_PANEL)

  const percentageText =
    setCompletionPercentage === 100 && !correctPercentage
      ? t('alt_texts.completed')
      : `${correctPercentage}% ${t('correct')}`

  return (
    <>
      <div className={`flex justify-between items-center pt-3 ${className}`}>
        <div className="flex items-center space-x-2">
          <ActivitySetProgress percentage={setCompletionPercentage} />
          <p>{t('set', { n: setNo })}</p>
        </div>
        <p>{percentageText}</p>
        <div className="flex space-x-2">
          <SVGImage
            alt={t('alt_texts.chart')}
            imageClassName="cursor-pointer"
            onClick={() => setShowBarChartModal(true)}
            srcKey="scoreboard/stat-icon.svg"
          />
          {!isFamilyView && (
            <>
              {setCompletionPercentage === 100 ? (
                <SVGImage
                  alt={t('alt_texts.retake')}
                  imageClassName="cursor-pointer"
                  onClick={() => {
                    onRetakeActivity(activityPath)
                    retakeBtnClick(setNo)
                  }}
                  srcKey="scoreboard/retake-icon.svg"
                />
              ) : (
                <SVGImage
                  alt={t('alt_texts.go')}
                  imageClassName="cursor-pointer"
                  onClick={() => {
                    onContinueActivity(activityPath)
                  }}
                  srcKey="scoreboard/continue-icon.svg"
                />
              )}
            </>
          )}
        </div>
      </div>

      {showBarChartModal && (
        <ScoreboardActivityChart
          id={id}
          key={levelNo + setNo}
          levelNo={levelNo}
          locale={locale}
          retakeRound={retakeRound}
          setCompletionPercentage={setCompletionPercentage}
          setNo={setNo}
          setShowBarChartModal={setShowBarChartModal}
        />
      )}
    </>
  )
}
